
<div v-if="card">
  <section v-if="display === 'valueIntro'" class="intro">
    <h4>Let's figure out what your card is worth</h4>
    <p>Card Ladder offers a few different ways of estimating the value of your card. Which would you prefer?</p>

    <div class="cta-options">
      <div v-for="(option, i) in estimateOptions" :key="i">
        <cta-card
          :icon="option.icon"
          :title="option.title"
          :description="option.description"
          :active="activeOption && activeOption === option.id"
          @click="selectEstimateOption(option)"
        />

        <external-link v-if="option.learnMoreLink" :href="option.learnMoreLink">
          <span>Learn More</span>
        </external-link>
      </div>
    </div>
  </section>

  <section v-if="display === 'valueGradedCardAutomatic'">
    <div class="intro">
      <h4>Help us figure out which card this is</h4>
      <p>Input the graded card cert # and the grader.</p>
    </div>

    <div class="form">
      <text-input
        v-model="certInput"
        label="Cert #"
        placeholder="63444200"
      />

      <select-input
        v-model="certGraderInput"
        label="Grader"
        :options="graderOptions"
      />
    </div>

    <portal to="collection-edit-card-modal-footer">
      <align small-gap>
        <btn
          :disabled="!certInput || !certGraderInput"
          :loading="saving"
          @click="fetchGradedCardInfo"
        >
          Save
        </btn>

        <btn kind="secondary" @click="goBack">
          Back
        </btn>
      </align>
    </portal>
  </section>

  <section v-if="display === 'valuePlayer'">
    <div class="intro">
      <h4>Help us figure out the player on this card</h4>
      <p>Search for the player below, and once selected, we'll do the rest. If you can't find the player, go back and try the another estimation method.</p>
    </div>

    <div class="form">
      <player-search @select="setPlayer" />
    </div>

    <portal to="collection-edit-card-modal-footer">
      <align small-gap>
        <btn kind="secondary" @click="goBack">
          Back
        </btn>
      </align>
    </portal>
  </section>

  <section v-if="display === 'valueMultipliers'">
    <div class="intro">
      <h4>Select one or more cards</h4>
      <p>We will help determine the ratio of value against your card.</p>
    </div>

    <div class="form">
      <card-multipliers
        v-model="card.multipliers"
        :card="card"
      />
    </div>

    <portal to="collection-edit-card-modal-footer">
      <align small-gap>
        <btn
          :disabled="!hasCompleteMultiplier"
          :loading="saving"
          @click="submitMultipliers"
        >
          Save
        </btn>

        <btn kind="secondary" @click="goBack">
          Back
        </btn>
      </align>
    </portal>
  </section>

  <section v-if="display === 'valueAdvanced'" class="advanced">
    <div class="intro">
      <h4>Index + Manual Sale</h4>
      <p>In order to estimate the value of your card we need to collect three pieces of information. The index you'd like to use and a known value at a given point in time. Given those three things we can map out the estimated value over time, tracking against the index you selected.</p>
    </div>

    <div class="grid">
      <section>
        <h5>First, let's choose an index</h5>

        <index-search
          @selectPlayer="setPlayer"
          @selectIndex="setIndex"
        />

        <div v-if="selectedIndex" class="index-list-container">
          <index-list-item :index="selectedIndex" />
        </div>

        <br />
        <h5>Next, let's use a past sale for the value and date</h5>
        <p>This can be any sale that you think is related to your card. Ideally you would find an exact match but you can be creative. Once you choose a sale we will fill out the value and date fields for you. Feel free to adjust either.</p>

        <sales-history-search @select="handleSaleSelected" />

        <br />

        <p>You can also enter in anything you want here. If you bought the card raw and got it graded PSA 10, feel free to enter in the value you think it is today and we'll update it for you going forward.</p>

        <div class="input-group">
          <currency-input
            v-model="card.playerIndexPrice"
            label="Value"
          />

          <date-input
            v-model="card.playerIndexDate"
            label="Date"
          />
        </div>
      </section>

      <section>
        <div v-if="card" class="checks">
          <align :class="{ success: Boolean(card.playerIndexId || card.otherIndexId) }">
            <icon class="check" name="check_circle" />
            <h5 class="secondary-font">
              Index
            </h5>
          </align>

          <align :class="{ success: Boolean(card.playerIndexPrice) }">
            <icon class="check" name="check_circle" />
            <h5 class="secondary-font">
              Value
            </h5>
          </align>

          <align :class="{ success: Boolean(card.playerIndexDate) }">
            <icon class="check" name="check_circle" />
            <h5 class="secondary-font">
              Date
            </h5>
          </align>
        </div>

        <card-list-item :card="card" />

        <div v-if="guideValue" class="value-estimate">
          <value-display
            estimated
            size="large"
            :value="$filters.currency(guideValue.value, { full: true })"
          />

          <div v-if="guideValue.description" class="description">
            {{ guideValue.description }}
          </div>
        </div>
      </section>
    </div>

    <portal to="collection-edit-card-modal-footer">
      <align small-gap>
        <btn
          :disabled="!hasCompleteAdvanced"
          :loading="saving"
          @click="submitAdvanced"
        >
          Save
        </btn>

        <btn kind="secondary" @click="goBack">
          Back
        </btn>
      </align>
    </portal>
  </section>

  <section v-if="display === 'valueLastSale'" class="lastsale">
    <div class="intro">
      <h4>Custom Sales Query</h4>
      <p>In order to determine the value using this method we need to construct a query on our Sales History database that retrieves only your card. Then we will use the most recent sale to determine the value of your card. You may also select an index to project the most recent sale's value forward if you'd like. Each night we automatically pull in the latest sales of your card and update the value of your card.</p>
    </div>

    <div class="grid">
      <section>
        <div>
          <pill-dropdown
            v-model="lastSoldOptionsValue"
            align="left"
            :options="lastSoldOptions"
          />
        </div>

        <br />

        <div v-show="lastSoldOptionsValue === 'index'">
          <index-search
            @selectPlayer="setPlayer"
            @selectIndex="setIndex"
          />

          <div v-if="selectedIndex" class="index-list-container">
            <index-list-item :index="selectedIndex" />
          </div>
        </div>

        <br />

        <p>Read <a href="https://cardladder.zendesk.com/hc/en-us/articles/12047786944663-12-Rules-for-Writing-Search-Queries" target="_blank">this document</a> written by our team to help you build an accurate query.</p>

        <text-area-input v-model="card.ebayQuery" label="Query" />

        <div v-if="card.ebayQuery">
          <align class="results-header" small-gap>
            <h5>Search Results</h5>

            <icon-button
              as="router-link"
              target="_blank"
              :to="salesHistoryLink"
              icon="open_in_new"
              size="16"
              tooltip="Open in new tab"
            />
          </align>

          <div v-if="loadingSales" class="loading-container">
            <spinner inline />
          </div>

          <div v-else-if="sales.length > 0" class="sales-container">
            <sales-list-item
              v-for="item in sales"
              :key="item.itemId"
              :item="item"
              as="a"
              split
              :href="item.url"
              target="_blank"
            />
          </div>
        </div>
      </section>

      <section>
        <div v-if="card" class="checks">
          <align :class="{ success: Boolean(card.ebayQuery && sales.length) }">
            <icon class="check" name="check_circle" />
            <h5 class="secondary-font">
              Query
            </h5>
          </align>

          <align :class="{ success: lastSoldOptionsValue !== 'index' || Boolean(card.playerIndexId || card.otherIndexId) }">
            <icon class="check" name="check_circle" />
            <h5 class="secondary-font">
              Value Method
            </h5>
          </align>
        </div>

        <card-list-item :card="card" />

        <div v-if="guideValue" class="value-estimate">
          <value-display
            estimated
            size="large"
            :value="$filters.currency(guideValue.value, { full: true })"
          />

          <div class="description">
            {{ guideValue.description }}
          </div>
        </div>
      </section>
    </div>

    <portal to="collection-edit-card-modal-footer">
      <align small-gap>
        <btn
          :disabled="!hasCompleteLastSale || !sales.length || (lastSoldOptionsValue === 'index' && !card.playerIndexId && !card.otherIndexId)"
          :loading="saving"
          @click="submitLastSale"
        >
          Save
        </btn>

        <btn kind="secondary" @click="goBack">
          Back
        </btn>
      </align>
    </portal>
  </section>

  <section v-if="display === 'valueManual'">
    <div class="intro">
      <h4>Manual value estimation</h4>
      <p>Enter what you think the card is worth. Instead of automatically estimating the value of your card, Card Ladder will use the value you enter here.</p>
    </div>

    <div class="form">
      <currency-input
        v-model="card.estimatedCurrentValue"
        label="Estimated Value"
        placeholder="2000"
      />
    </div>

    <portal to="collection-edit-card-modal-footer">
      <align small-gap>
        <btn
          :disabled="!card.estimatedCurrentValue"
          :loading="saving"
          @click="submitManual"
        >
          Save
        </btn>

        <btn kind="secondary" @click="goBack">
          Back
        </btn>
      </align>
    </portal>
  </section>

  <section v-if="display === 'loading'">
    <div class="intro">
      <h4>Estimating the value...</h4><br />
      <spinner inline />
    </div>
  </section>
</div>
